import { FormEvent, useCallback, useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useLogin } from '../../../hooks/auth/useLogin'

import Icon from '../../ui/Icon'
import { Button } from '../../buttons/Button'
import { TextInput } from '../../input/TextInput'
import { PasswordInput } from '../../input/PasswordInput'

import { useApp } from '../../../contexts/AppContext'
import { FormField } from '../../../types/formData'
import { Credentials } from '../../../types/user/auth'
import { BannerKind } from '../../../types/contexts/AppContext'
import { InputType } from '../../../constants/inputTypes'
import { FeatureFlags, isFeatureEnabled } from '../../../constants/featureFlags'
import { CONFIG } from '../../../config'

interface LoginFormData {
  email: FormField<string>
  password: FormField<string>
}

const initLoginFormData = () => {
  return {
    email: { value: '', isValid: false },
    password: { value: '', isValid: false },
  }
}

export const LoginForm = () => {
  const { t } = useTranslation(['login', 'guestHeader'])
  const [formData, setFormData] = useState<LoginFormData>(initLoginFormData())
  const navigate = useNavigate()
  const { showBanner } = useApp()

  // Google sign-in returns to /login?code=… and is completed by the Login page, which shows a loader meanwhile.
  const appLogin = useLogin()

  const resetState = useCallback(() => {
    appLogin.resetState()
  }, [appLogin])

  useEffect(() => {
    if (appLogin.error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: appLogin.error,
      })
      resetState()
    }
  }, [appLogin, resetState, showBanner])

  const handleInputChange = (name: string, value: any, isValid: boolean) => {
    setFormData(prev => ({ ...prev, [name]: { value, isValid } }))
  }

  const validForm = () => Object.values(formData).every(input => input.isValid)

  const handleLogin = async () => {
    const response = await appLogin.login({
      email: formData.email.value,
      password: formData.password.value,
    } as Credentials)
    if (response) {
      resetState()
      setFormData(initLoginFormData())
      navigate('/dashboard')
    }
  }

  /** Enter in a field submits the form like the button; nothing happens while invalid or already signing in. */
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (validForm() && !appLogin.loading) {
      void handleLogin()
    }
  }

  const handleGoogleLogin = async () => {
    window.location.href = `${CONFIG.BOUNCE_API}/oauth2/authorization/google`
  }

  return (
    <form className="fly-form" onSubmit={handleSubmit}>
      <Button
        className="fly-btn-back"
        variant="ghost"
        size="sm"
        icon={<Icon name="chevL" size={14} />}
        label={t('guestHeader:btn.back')}
        onClick={() => navigate('/')}
      />

      <div className="eyebrow">{t('login:eyebrow')}</div>
      <h1 className="display fly-text-title">{t('login:title')}</h1>
      <p className="fly-text-subtitle">{t('login:subtitle')}</p>

      {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
        <>
          <Button
            className="btn-google"
            variant="ghost"
            block
            icon={<Icon name="google" size={18} />}
            label={t('login:google-btn')}
            onClick={handleGoogleLogin}
          />
          <div className="divider-or">{t('login:divider')}</div>
        </>
      )}

      <div className="ph-col">
        <TextInput
          name="email"
          label={t('auth:email')}
          required={true}
          type={InputType.Email}
          placeHolder={t('auth:email')}
          value={formData.email.value}
          onChange={handleInputChange}
          infoMessage={t('login:email-info')}
          errorMessage={t('login:email-error')}
        />
        <PasswordInput
          name="password"
          label={t('auth:password')}
          value={formData.password.value}
          placeHolder={t('auth:password')}
          onChange={handleInputChange}
        />
      </div>

      <Button
        className="btn-submit"
        type="submit"
        size="lg"
        block
        loading={appLogin.loading}
        disabled={!validForm()}
        label={appLogin.loading ? t('login:loading') : t('auth:switch.login')}
        icon={<Icon name="arrowR" size={16} />}
        iconPosition="right"
      />

      <p className="gfly-footer">
        {t('login:footer-text')}{' '}
        <Link to="/register">{t('login:footer-link')}</Link>
      </p>
    </form>
  )
}
